import React, { useEffect, useState, useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'
import injectStyles from 'react-jss'
import { Tooltip } from '@material-ui/core'
import { Col, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import EditIcon from '@material-ui/icons/Edit'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { ClientViewWrapper } from 'Components/Clients/ClientView/ClientViewWrapper'
import { PageTitle } from 'Components/PageTitle/index'
import { SectionLabel } from 'Components/Section/SectionLabel/index'
import { SectionTitle } from 'Components/Section/SectionTitle/index'
import { SectionWrapper } from 'Components/Section/SectionWrapper/index'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import StyledIconButton from 'Components/Form/StyledIconButton'
import ClientsIcon from 'Assets/Images/icons/clients'
import { PATHS } from 'Config/Routing/paths'
import { EDDM_SIGNUP } from '../../../shared/constants'
import NewCampaignDropdown from '../../../Components/NewCampaignDropdown'
import CampaignsBlock from './CampaignsBlock'
import SftpFilesBlock from './SftpFilesBlock'
import { getClientById } from './gql'
import { styles } from './styles'
import XeroClient from '../XeroClient'
import { apiXero } from '../../../Module/API/Xero'
import { Box } from '@material-ui/core'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import TaxExemptionSection from '../TaxExemption'
import { CONFIG } from '../../../Config/config'

const BreadcrumbsClientView = ({ name }) => (
  <div>
    <StyledBreadcrumbs
      icon={<ClientsIcon style={{ width: 24 }} />}
      breadcrumbs={[
        {
          label: 'Clients',
          link: `${PATHS.main.dashboard}${PATHS.dashboard.clients}`,
        },
        { label: name },
      ]}
    />
  </div>
)

const ClientView = ({ classes, countries }) => {
  const match = useRouteMatch()
  const [client, setClient] = useState(null)
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [tenant, setTenant] = useState(null)
  const [isFetchedData, setIsFetchedData] = useState(false)
  const [xeroInfo, setXeroInfo] = useState({ isLoading: true })
  const [usersHashMap, setUsersHashMap] = useState({})
  const [xeroInitValues, setXeroInitValues] = useState({})
  const [
    uploadedDocumentsForUnitedStatesStates,
    setUploadedDocumentsForUnitedStatesStates,
  ] = useState([])
  const clientId = match.params.id
  const country = useMemo(
    () => client && countries.find(country => country.id === client.countryId),
    [client, countries],
  )
  const isEddmSignup = client && client.createdBy === EDDM_SIGNUP

  const isUS = country ? country.code === 'US' : false

  const fetchClient = async () => {
    const response = await getClientById(clientId)
    const { data } = await response.json()
    const client = data.clients.nodes[0]

    setUploadedDocumentsForUnitedStatesStates(data.getClientTaxExemptionByState)
    setClient(client)

    return client
  }

  const fetchTenant = async client => {
    const result = await apiXero.getXeroTenant({
      includeAvailableAccount: true,
      includeCurrencies: true,
    })

    if (result && result.isAuthorized && result.isConnected) {
      if (client) {
        const contactData = await apiXero.getXeroContact({
          tenantId: result.tenantId,
          contactName: client.name,
          contactId: clientId,
        })

        if (contactData.isExist) {
          const contact = contactData.contact
          setXeroInfo({
            isLoading: false,
            entity: result.name,
            contact: contact.name,
          })

          const contactPersons = {
            ...contact.contactPersons.reduce((acc, person, index) => {
              acc[index + 1] = {
                firstName: {
                  value: person.firstName,
                  error: false,
                  required: false,
                },
                lastName: {
                  value: person.lastName,
                  error: false,
                  required: false,
                },
                email: {
                  value: person.emailAddress,
                  error: false,
                  required: false,
                },
                includeInEmails: {
                  value: person.includeInEmails,
                  error: false,
                },
              }

              return acc
            }, {}),
          }

          const addresses = contact.addresses.find(
            item => item.addressType === 'STREET',
          )

          const salesDefaultAccountCode =
            result && result.availableAccount
              ? `${result.availableAccount.code} ${
                  result.availableAccount.name
                }`
              : ''

          const trackingCategory = contact.salesTrackingCategories
            ? contact.salesTrackingCategories.find(
                item => item.trackingCategoryName === 'Clients',
              )
            : null

          setXeroInitValues({
            firstName: contact.firstName,
            lastName: contact.lastName,
            defaultCurrency: contact.defaultCurrency,
            emailAddress: contact.emailAddress,
            address1: addresses ? addresses.addressLine1 : '',
            address2: addresses ? addresses.addressLine2 : '',
            address3: addresses ? addresses.addressLine3 : '',
            address4: addresses ? addresses.addressLine4 : '',
            city: addresses ? addresses.city : '',
            country: addresses ? addresses.country : '',
            region: addresses ? addresses.region : '',
            postalCode: addresses ? addresses.postalCode : '',
            salesDefaultLineAmountType: 'EXCLUSIVE',
            trackingCategoryName: trackingCategory
              ? trackingCategory.trackingOptionName
              : '',
            salesDefaultAccountCode,
          })

          setUsersHashMap(contactPersons)
        } else {
          if (contactData.closestContact) {
            setXeroInfo({
              isLoading: false,
              entity: result.name,
              contact: null,
              closestContact: contactData.closestContact,
            })
          } else {
            setXeroInfo({
              isLoading: false,
              entity: result.name,
              contact: null,
              closestContact: null,
            })
          }
        }

        const currencies =
          result.availableCurrencies.map(item => ({
            label: item,
            value: item,
          })) || []
        setTenant({ ...result, currencies })
      }
    } else {
      setXeroInfo({
        isLoading: false,
        entity: null,
        contact: null,
        closestContact: null,
      })
    }
  }
  const fetchAllData = async () => {
    setLoading(true)
    const client = await fetchClient()
    try {
      await fetchTenant(client)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ err })
      setXeroInfo({ isLoading: false, error: 'Something went wrong' })
    } finally {
      setLoading(false)
      setIsFetchedData(true)
    }
  }

  useEffect(() => {
    if (!isFetchedData) {
      fetchAllData()
    }
  }, [])

  useEffect(() => {
    if (client) {
      setSections([
        {
          name: 'Name',
          value: <div>{client.name}</div>,
          xs: 2,
        },
        {
          name: 'Country',
          value: <div>{country ? country.name : 'N/A'}</div>,
          xs: 2,
        },
        {
          name: 'Client Status',
          value: (
            <div>
              {client.status === 'DELETED' ? 'ARCHIVED' : client.status}
            </div>
          ),
          xs: 2,
        },
        {
          name: 'Self-Serve enabled',
          value: <div>{client.isSelfServeEnabled ? 'Yes' : 'No'}</div>,
          xs: 2,
        },
        ...(client.isSelfServeEnabled
          ? [
              {
                name: 'EDDM Only',
                value: <div>{isEddmSignup ? 'Yes' : 'No'}</div>,
                xs: 2,
              },
            ]
          : []),
      ])
    }
  }, [client, country])

  const isXeroAvailable = apiXero.isHaveXeroAccess()

  return (
    <LayoutDashboard>
      <HideWhenBlocked>
        <ClientViewWrapper>
          <BreadcrumbsClientView name={client ? client.name : ''} />
          <PageTitle className="page-title">
            {(client && client.name) || '\u00A0'}
            {client && client.isSelfServeEnabled && (
              <Tooltip
                title={
                  isEddmSignup
                    ? 'Campaign creation is disabled as this client is EDDM-only. To proceed, disable the EDDM-only option in their settings.'
                    : ''
                }
              >
                <Box style={{ position: 'absolute', top: '0', right: '56px' }}>
                  <NewCampaignDropdown
                    countryId={client.countryId}
                    clientId={client.id}
                    selfServeEnabled={client.isSelfServeEnabled}
                    disabled={isEddmSignup}
                  />
                </Box>
              </Tooltip>
            )}
            <Link
              to={`${PATHS.main.dashboard}${
                PATHS.dashboard.clients
              }/${clientId}/edit`}
              className="link-edit"
            >
              <StyledIconButton>
                <EditIcon />
              </StyledIconButton>
            </Link>
          </PageTitle>
          <SectionWrapper className="section-wrapper first">
            <SectionTitle className="section-title">
              Client Details
            </SectionTitle>
            <Row xs={12}>
              {loading || !sections.length ? (
                <LoadingIndicator />
              ) : (
                sections.map(section => (
                  <Col key={section.name} xs={section.xs}>
                    <SectionLabel className="section-label">
                      {section.name}
                    </SectionLabel>
                    {section.value}
                  </Col>
                ))
              )}
            </Row>
          </SectionWrapper>
          {isUS && CONFIG.US_TAXES_ENABLED && (
            <TaxExemptionSection
              disableAction={true}
              isTaxExemptionEnabled={client && client.isTaxExemptionEnabled}
              uploadedDocumentsForUnitedStatesStates={
                uploadedDocumentsForUnitedStatesStates
              }
            />
          )}
          <CampaignsBlock client={client || {}} />

          {client && client.sftpEnabled && (
            <SftpFilesBlock clientId={clientId} countryCode={country?.code} />
          )}

          {client ? (
            <XeroClient
              isXeroAvailable={isXeroAvailable}
              xeroInfo={xeroInfo}
              tenant={tenant}
              xeroInitValues={xeroInitValues}
              currencies={tenant ? tenant.currencies : []}
              usersHashMap={usersHashMap}
              isEditMode={false}
            />
          ) : null}
        </ClientViewWrapper>
      </HideWhenBlocked>
    </LayoutDashboard>
  )
}

export default injectStyles(styles)(ClientView)
